<template>
  <SettingsPaneV2 header="Members">
    <template #subheader>
      Manage members and users of your workspace and set their access level.
    </template>
    <template #body>
      <TabSelector
        class="tabs"
        :options="tabsOptions"
        :value="tabOption"
        @change="setTab"
      />
      <InviteForm />

      <Invitation
        v-for="invitation in sortedInvitations"
        :key="`invitation-${invitation.id}`"
        :invitation="invitation"
        @update="updateInvitation"
        @delete="deleteInvitation"
      />

      <template v-if="canManageNonAnnotators">
        <Member
          v-for="member in sortedMembers"
          :key="`member-${member.id}`"
          :member="member"
          @update="updateMembership"
          @delete="deleteMembership"
        />
      </template>

      <Member
        v-for="member in sortedAnnotators"
        :key="`annotator-${member.id}`"
        :member="member"
        @update="updateMembership"
        @delete="deleteMembership"
      />

      <template v-if="teamStore.currentTeamIsClient">
        <ReadOnlyMember
          v-for="member in partnerMembers"
          :key="`client-${member.id}`"
          :member="member"
        />
      </template>
    </template>

    <template #footer />

    <ConfirmDialog
      v-model:open="deleteInvitationConfirmationDialogOpen"
      class-name="delete-invitation-config-dialog"
      type="error"
      @confirm="deleteInvitationConfirm"
    >
      <template #title> {{ deleteInvitationTitle }} </template>
      <template #detail>
        This will prevent the user from clicking the invitation link they got in the email and
        joining the workspace.
      </template>
      <template #confirm-text> Cancel invitation </template>
    </ConfirmDialog>

    <ConfirmDialog
      v-model:open="deleteMembershipConfirmationDialogOpen"
      class-name="delete-membership-config-dialog"
      type="error"
      @confirm="deleteMembershipConfirm"
    >
      <template #title>
        Please confirm you wish to remove {{ memberToDeleteName }} from the workspace.
      </template>
      <template #detail>
        Removing a user from the workspace can only by undone by inviting them back.
      </template>
      <template #confirm-text> Remove from workspace </template>
    </ConfirmDialog>

    <ConfirmDialog
      v-model:open="transferOwnershipConfirmationDialogOpen"
      type="warn"
      class-name="transfer-ownership-config-dialog"
      @confirm="transferOwnershipConfirm"
    >
      <template #title>
        Transfer team ownership to {{ memberToTransferOwnershipToName }}?
      </template>
      <template #detail>
        {{ memberToTransferOwnershipToName || 'This user' }} will become responsible for the team's
        billing, and will be able to invite and remove admins. Be careful when transferring
        ownership!
      </template>
    </ConfirmDialog>
  </SettingsPaneV2>
</template>

<script lang="ts">
export default {
  name: 'TeamMembers',
}
</script>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'

import ConfirmDialog from '@/uiKit/Dialog/ConfirmDialog.vue'
import { useAuth } from '@/modules/Auth/useAuth'
import { useToast } from '@/uiKit/Toast/useToast'
import SettingsPaneV2 from '@/layouts/Main/SettingsDialog/Panes/SettingsPaneV2.vue'
import { useAuthStore } from '@/modules/Auth/useAuthStore'
import { useTeamStore } from '@/pinia/useTeamStore'
import type { InvitationPayload, MembershipPayload } from '@/store/types'
import { MembershipRole } from '@/store/types'
import { getFullName } from '@/core/utils/formatter'
import TabSelector from '@/components/Common/TabSelector/TabSelector.vue'

import InviteForm from './InviteForm.vue'
import Invitation from './Invitation.vue'
import Member from './Member.vue'
import ReadOnlyMember from './ReadOnlyMember.vue'
import { ROLE_ORDER, ROLE_OPTIONS } from '@/modules/Teams/roleData'
import { useInviteStore } from '@/modules/Teams/useInviteStore'
import { useMembershipStore } from '@/modules/Teams/useMembershipStore'
import { assert } from '@/core/utils/assert'
import { useUserStore } from '@/modules/Auth/useUserStore'

const authStore = useAuthStore()
const teamStore = useTeamStore()
const membershipStore = useMembershipStore()
const inviteStore = useInviteStore()
const toast = useToast()

const { isAuthorized } = useAuth()

const tabOption = ref('all')
const userStore = useUserStore()

const visibleRoles = computed(() =>
  userStore.isWorkforceManager
    ? [MembershipRole.ANNOTATOR, MembershipRole.WORKFORCE_MANAGER]
    : Object.values(MembershipRole),
)

const tabsOptions = computed(() => [
  { value: 'all', label: 'All members' },
  ...ROLE_OPTIONS.filter(({ id }) => visibleRoles.value.includes(id)).map(({ id, text }) => ({
    value: id,
    label: text,
  })),
])

const setTab = (value: string): void => {
  tabOption.value = value
}

const canManageNonAnnotators = computed<boolean>(() =>
  isAuthorized('update_membership', { subject: 'membership', resource: { role: 'member' } }),
)

onMounted(async () => {
  if (!isAuthorized('view_invitations')) {
    return
  }
  const teamSlug = assert(teamStore.currentTeam)?.slug
  await inviteStore.getUnconfirmedInvitations(teamSlug)
})

const tabOptionFilter = (member: InvitationPayload | MembershipPayload): boolean =>
  tabOption.value === 'all' || member.role === tabOption.value

const sortMembers = (a: MembershipPayload, b: MembershipPayload): number => {
  if (tabOption.value === 'all') {
    return ROLE_ORDER[a.role] - ROLE_ORDER[b.role]
  }

  const aName = getFullName(a).toLowerCase()
  const bName = getFullName(b).toLowerCase()

  if (aName < bName) {
    return -1
  }

  if (aName > bName) {
    return 1
  }

  return 0
}

const sortedInvitations = computed(() =>
  inviteStore.invitations
    .filter((i) => i.team_id === teamStore.currentTeam?.id)
    .filter(tabOptionFilter)
    .filter((i) => visibleRoles.value.includes(i.role))
    .sort((a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role]),
)

const FULL_ROLES = [MembershipRole.MEMBER, MembershipRole.ADMIN, MembershipRole.OWNER]

const sortedMembers = computed(() =>
  teamStore.currentTeamMemberships
    .filter((member) => FULL_ROLES.some((r) => r === member.role))
    .filter((member) => visibleRoles.value.includes(member.role))
    .filter(tabOptionFilter)
    .sort(sortMembers),
)

const WORKFORCE_ROLES = [MembershipRole.ANNOTATOR, MembershipRole.WORKFORCE_MANAGER]

const sortedAnnotators = computed(() =>
  teamStore.currentTeamMemberships
    .filter((member) => WORKFORCE_ROLES.some((r) => r === member.role))
    .filter(tabOptionFilter)
    .sort(sortMembers),
)

const partnerMembers = computed(() =>
  teamStore.allRelevantMemberships
    .filter((m) => m.team_id === teamStore.currentTeam?.partner_id)
    .filter((m) => visibleRoles.value.includes(m.role))
    .filter(tabOptionFilter)
    .sort(sortMembers),
)

// delete invitation
const deleteInvitationConfirmationDialogOpen = ref(false)

const invitationToDelete = ref<InvitationPayload | null>(null)

const deleteInvitationTitle = computed<string>(() => {
  const email = invitationToDelete.value?.email
  return email
    ? `Please confirm you wish to cancel the invitation sent to ${email}.`
    : 'Please confirm you wish to cancel this invitation.'
})

const deleteInvitation = (invitation: InvitationPayload): void => {
  invitationToDelete.value = invitation
  deleteInvitationConfirmationDialogOpen.value = true
}

const deleteInvitationConfirm = async (): Promise<void> => {
  deleteInvitationConfirmationDialogOpen.value = false

  if (!invitationToDelete.value) {
    return
  }

  const currentTeamId = teamStore.currentTeam?.id
  if (!currentTeamId) {
    return
  }
  await inviteStore.deleteInvitationById(currentTeamId, invitationToDelete.value.id)

  invitationToDelete.value = null
}

// update invitation
const updateInvitation = async (params: {
  invitation: InvitationPayload
  newRole: InvitationPayload['role']
}): Promise<void> => {
  await inviteStore.updateInvitationRole(params.invitation.id, params.newRole)
}

// delete membership
const deleteMembershipConfirmationDialogOpen = ref(false)

const memberToDelete = ref<MembershipPayload | null>(null)

const memberToDeleteName = computed(
  () => (memberToDelete.value && getFullName(memberToDelete.value)) || null,
)

const deleteMembership = (member: MembershipPayload): void => {
  memberToDelete.value = member
  deleteMembershipConfirmationDialogOpen.value = true
}

const deleteMembershipConfirm = async (): Promise<void> => {
  deleteMembershipConfirmationDialogOpen.value = false

  if (!memberToDelete.value) {
    return
  }

  await membershipStore.deleteMembershipById(memberToDelete.value.id)

  memberToDelete.value = null
}

// transfer ownership
const transferOwnershipConfirmationDialogOpen = ref(false)

const memberToTransferOwnershipTo = ref<MembershipPayload | null>(null)

const memberToTransferOwnershipToName = computed(
  () =>
    (memberToTransferOwnershipTo.value && getFullName(memberToTransferOwnershipTo.value)) || null,
)

const transferOwnershipConfirm = async (): Promise<void> => {
  transferOwnershipConfirmationDialogOpen.value = false

  if (!memberToTransferOwnershipTo.value) {
    return
  }
  const result = await membershipStore.updateMembershipRole(
    memberToTransferOwnershipTo.value.id,
    'owner',
  )

  if (!result.success) {
    return
  }

  // relogin so we get new state, memberships, etc
  authStore.loginWithToken()

  toast.success({
    meta: { title: 'Ownership successfully transferred' },
  })

  memberToTransferOwnershipTo.value = null
}

const updateMembership = async (params: {
  membership: MembershipPayload
  newRole: MembershipRole
}): Promise<void> => {
  const { membership, newRole } = params

  if (!newRole || newRole === membership.role) {
    return
  }

  if (newRole === 'owner') {
    memberToTransferOwnershipTo.value = membership
    transferOwnershipConfirmationDialogOpen.value = true
    return
  }

  await membershipStore.updateMembershipRole(membership.id, newRole)
}
</script>

<style lang="scss" scoped>
.tabs {
  padding-bottom: 16px;
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
/*  This `DarwinDialog` modal is normally called from `<modal>` and it needs to be on top of it.
    There is no current support to control z-index, so the below line is a workaround.
    `.delete-invitation-config-dialog`, `.delete-membership-config-dialog`, `.transfer-ownership-config-dialog`
    is added to the dialog so we make sure the leaked css isn't
    changing the `z-index` of other dialogs.
*/
.darwin-dialog.delete-invitation-config-dialog,
.darwin-dialog.delete-membership-config-dialog,
.darwin-dialog.transfer-ownership-config-dialog {
  z-index: var(--z-index-modal-level-2-overlay) !important;
}
</style>
